import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AddressBook, EnvelopeSimple, MagnifyingGlass, Phone, Plus } from "@phosphor-icons/react";
import { api, lies } from "../api/client";
import type { BeteiligterArt, Kontakt } from "../api/types";
import { Auswahl } from "../components/Auswahl";
import { Dialog, Feld, Meldung } from "../components/Bausteine";
import { BETEILIGTER_ART } from "../lib/format";
import { usePfad } from "../lib/pfad";

/** Suchtreffer aus dem Adressbuch, nach kurzer Tipppause (auch für den Beteiligten-Dialog). */
export function useKontakte(q: string, aktiv = true) {
  const [wort, setWort] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setWort(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  return useQuery({
    queryKey: ["kontakte", wort],
    queryFn: () => lies<Kontakt[]>(`/kontakte?q=${encodeURIComponent(wort)}`),
    enabled: aktiv,
  });
}

/** Zentrales Adressbuch (SPEC §16): Firmen und Ansprechpersonen über alle Projekte. */
export function Adressen() {
  usePfad([{ titel: "Adressbuch" }]);
  const [q, setQ] = useState("");
  const liste = useKontakte(q);
  const [offen, setOffen] = useState<Kontakt | "neu" | null>(null);
  return (
    <main className="seite">
      <div className="seite-kopf">
        <div>
          <h1>Adressbuch</h1>
          <div className="dim">Firmen und Ansprechpersonen aller Projekte – Änderungen gelten überall, wo sie beteiligt sind.</div>
        </div>
        <div className="luft" />
        <label className="suche">
          <MagnifyingGlass size={16} />
          <input className="eingabe" type="search" autoFocus placeholder="Firma, Name, Ort, E-Mail …" value={q}
                 onChange={(e) => setQ(e.target.value)} />
        </label>
        <button className="knopf haupt" onClick={() => setOffen("neu")}><Plus size={16} weight="bold" /> Kontakt</button>
      </div>
      <section className="karte">
        <Meldung fehler={liste.error} />
        <div className="liste">
          {liste.data?.map((k) => (
            <button key={k.id} className="zeile zeile-knopf" onClick={() => setOffen(k)}>
              <span className="nr"><AddressBook size={18} /></span>
              <div className="haupt">
                <div>{[k.firma, k.name].filter(Boolean).join(" · ")}</div>
                <div className="mute">{[BETEILIGTER_ART[k.art], k.funktion, k.email, k.telefon || k.mobil, k.ort].filter(Boolean).join(" · ")}</div>
              </div>
              <span className="kontakt-projekte">
                {k.projekte.slice(0, 3).map((p) => <span key={p.id} className="chip">{p.name}</span>)}
                {k.projekte.length > 3 && <span className="chip">+{k.projekte.length - 3}</span>}
              </span>
            </button>
          ))}
          {liste.isSuccess && liste.data.length === 0 &&
            <div className="leer">{q ? "Nichts gefunden." : "Noch keine Kontakte. Beteiligte aus den Projekten landen hier von selbst."}</div>}
        </div>
      </section>
      {offen && <KontaktDialog k={offen === "neu" ? null : offen} zu={() => setOffen(null)} />}
    </main>
  );
}

type Form = Omit<Kontakt, "id" | "projekte">;
const LEER: Form = { firma: "", name: "", funktion: "", email: "", telefon: "", mobil: "", strasse: "", plz: "", ort: "", notiz: "", art: "auftragnehmer" };

function KontaktDialog({ k, zu }: { k: Kontakt | null; zu: () => void }) {
  const qc = useQueryClient();
  // Nur die Felder des Formulars, nicht `id` und `projekte` aus der Liste.
  const [f, setF] = useState<Form>(() => (k
    ? Object.fromEntries(Object.keys(LEER).map((s) => [s, k[s as keyof Form] ?? LEER[s as keyof Form]])) as Form
    : LEER));
  const fertig = () => { for (const s of [["kontakte"], ["beteiligte"], ["leistungen"]]) qc.invalidateQueries({ queryKey: s }); zu(); };
  const speichern = useMutation({
    mutationFn: () => (k ? api("PATCH", `/kontakte/${k.id}`, f) : api("POST", "/kontakte", f)),
    onSuccess: fertig,
  });
  const loeschen = useMutation({ mutationFn: () => api("DELETE", `/kontakte/${k!.id}`), onSuccess: fertig });
  const feld = (s: keyof Form, titel: string, breit = false, typ = "text") => (
    <Feld titel={titel} breit={breit}>
      <input className="eingabe" type={typ} value={String(f[s])} onChange={(e) => setF({ ...f, [s]: e.target.value })} />
    </Feld>
  );
  return (
    <Dialog offen zu={zu} titel={k ? [k.firma, k.name].filter(Boolean).join(" · ") : "Neuer Kontakt"} fuss={<>
      {k && k.projekte.length === 0 && (
        <button className="knopf leise gefahr" style={{ marginRight: "auto" }}
                onClick={() => confirm("Diesen Kontakt löschen?") && loeschen.mutate()}>Löschen</button>
      )}
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!(f.firma.trim() || f.name.trim()) || speichern.isPending}
              onClick={() => speichern.mutate()}>Speichern</button>
    </>}>
      <div className="raster">
        {feld("firma", "Firma / Behörde", true)}
        {feld("name", "Ansprechperson")}
        {feld("funktion", "Funktion")}
        {feld("email", "E-Mail", false, "email")}
        {feld("telefon", "Telefon", false, "tel")}
        {feld("mobil", "Mobil", false, "tel")}
        <Feld titel="Übliche Rolle">
          <Auswahl value={f.art} onChange={(e) => setF({ ...f, art: e.target.value as BeteiligterArt })}>
            {Object.entries(BETEILIGTER_ART).map(([a, t]) => <option key={a} value={a}>{t}</option>)}
          </Auswahl>
        </Feld>
        {feld("strasse", "Straße", true)}
        {feld("plz", "PLZ")}
        {feld("ort", "Ort")}
        <Feld titel="Notiz" breit>
          <textarea className="eingabe" rows={2} value={f.notiz} onChange={(e) => setF({ ...f, notiz: e.target.value })} />
        </Feld>
      </div>
      {k && (k.email || k.telefon) && (
        <div className="punkt-leiste" style={{ marginTop: 10 }}>
          {k.email && <a className="knopf leise" href={`mailto:${k.email}`}><EnvelopeSimple size={15} /> Mail</a>}
          {(k.telefon || k.mobil) && <a className="knopf leise" href={`tel:${(k.mobil || k.telefon).replace(/\s/g, "")}`}><Phone size={15} /> Anrufen</a>}
        </div>
      )}
      {k && k.projekte.length > 0 && (
        <p className="mute">Beteiligt in: {k.projekte.map((p, i) => (
          <span key={p.id}>{i > 0 && ", "}<Link to={`/p/${p.kurz}/beteiligte`} onClick={zu}>{p.name}</Link> ({BETEILIGTER_ART[p.art]})</span>
        ))}. Änderungen an Firma, Name, Funktion, E-Mail und Telefon gelten dort ab sofort; freigegebene Protokolle bleiben, wie sie sind.</p>
      )}
      <Meldung fehler={speichern.error || loeschen.error} />
    </Dialog>
  );
}
